import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import BrandLogo from "./BrandLogo";
import MenuIcon from "./MenuIcons";
import { allowedToView } from "../utils/PermissionUtils";
import { useUnread } from "../utils/unread";

function Sidebar({ collapsed = false, onToggleCollapse }) {
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const sidebarRef = useRef(null);
    const toggleButtonRef = useRef(null); // Reference for the toggle button
    const location = useLocation();
    const userRole = localStorage.getItem("userRole");
    const isAdmin = allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator']);
    const isSuperAdmin = allowedToView(userRole, ['superadmin']);
    // Admins and super admins see everyone's requests to serve; other members see their own.
    const seesAllRequests = allowedToView(userRole, ['admin', 'superadmin']);
    // Admins and super admins see the whole issue log; other members see the issues they are responsible for.
    const seesIssueLog = seesAllRequests;
    const unread = useUnread();

    // Toggle sidebar
    const toggleSidebar = () => {
        setSidebarOpen(!isSidebarOpen);
    };

    // Close sidebar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                sidebarRef.current &&
                !sidebarRef.current.contains(event.target) && // Exclude sidebar
                toggleButtonRef.current &&
                !toggleButtonRef.current.contains(event.target) // Exclude toggle button
            ) {
                setSidebarOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Close sidebar on navigation
    useEffect(() => {
        setSidebarOpen(false);
    }, [location]);

    const isCurrent = (to, exact) => (
        exact ? location.pathname === to : location.pathname === to || location.pathname.startsWith(`${to}/`)
    );

    // Each item has an icon and a name. When the menu is folded only the icon shows;
    // pointing at it (or reaching it with the keyboard) shows the name beside it.
    // An item stays lit while any page under it is open, unless `exact` is set.
    // `badge` is a count shown at the end of the item (unread notifications).
    const item = (to, label, icon, { exact = false, under = to, badge = 0 } = {}) => (
        <li>
            <Link
                to={to}
                className={isCurrent(under, exact) ? "is-current" : ""}
                data-tip={label}
                aria-label={badge > 0 ? `${label}, ${badge} unread` : label}
            >
                <MenuIcon name={icon} />
                <span className="cf-menu-label">{label}</span>
                {badge > 0 && <span className="cf-menu-badge" aria-hidden="true">{badge > 99 ? "99+" : badge}</span>}
            </Link>
        </li>
    );

    // A smaller item under "Members": the same list, already filtered.
    const subItem = (to, label) => (
        <li>
            <Link to={to} className={isCurrent(to, true) ? "is-current" : ""}>{label}</Link>
        </li>
    );

    return (
        <>
            <button
                ref={toggleButtonRef} // Attach ref to toggle button
                className="button is-light toggle-sidebar-btn primary-bg"
                onClick={toggleSidebar}
                aria-label="Toggle Sidebar"
            >
                {isSidebarOpen ? "✕" : "☰"}
            </button>
            <aside
                ref={sidebarRef}
                className={`menu pb-6 sidebar ${isSidebarOpen ? "is-active" : ""} ${collapsed ? "is-collapsed" : ""}`}
            >
                <div className="sidebar-logo-section">
                    <div className="cf-side-top">
                        <Link to="/" className="logo-link" aria-label="CFCM Portal home">
                            {/* The logo is smaller when the menu is folded. */}
                            <BrandLogo size={collapsed ? 16 : 44} />
                        </Link>

                        {/* On a computer the menu folds down to a narrow strip. On a phone the
                            menu is opened and closed with the button at the top of the screen. */}
                        {onToggleCollapse && (
                            <button
                                type="button"
                                className="cf-side-collapse"
                                onClick={onToggleCollapse}
                                aria-expanded={!collapsed}
                                aria-label={collapsed ? "Show the menu" : "Hide the menu"}
                                title={collapsed ? "Show the menu" : "Hide the menu"}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    {collapsed
                                        ? <path d="M9 6l6 6-6 6" />
                                        : <path d="M15 6l-6 6 6 6" />}
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
                <div className="sidebar-menu">
                    <ul className="menu-list">
                        {item("/dashboard", "Dashboard", "dashboard", { exact: true })}
                        {item("/dashboard/notifications", "Notifications", "notifications", { badge: unread })}
                        {item("/dashboard/missions", "Missions", "missions")}

                        {isAdmin && (
                            <>
                                {item("/dashboard/members/all", "Members", "members", { under: "/dashboard/members" })}
                                <li className="cf-menu-sub">
                                    <ul>
                                        {subItem("/dashboard/members/active", "Active")}
                                        {subItem("/dashboard/members/inactive", "Inactive")}
                                        {subItem("/dashboard/members/unapproved", "Unapproved")}
                                    </ul>
                                </li>
                            </>
                        )}

                        {item("/dashboard/events", "Events", "events")}
                        {item("/dashboard/schools", "Schools", "schools")}
                        {item("/dashboard/prayer-items", "Prayer Items", "feedback")}
                        {item("/dashboard/requests-to-serve", seesAllRequests ? "Requests to Serve" : "My Requests to Serve", "requests")}
                        {item("/dashboard/issues", seesIssueLog ? "Issue Log" : "My Issues", "issues")}
                        {isAdmin && item("/dashboard/bookings", "Makuyu Booking", "bookings")}
                        {item("/dashboard/documents", "Documents", "documents")}
                        {item("/dashboard/books", "Mission Books", "books")}
                    </ul>

                    <p className="menu-label cf-menu-heading">Settings</p>
                    <ul className="menu-list">
                        {item("/dashboard/chapters", "Chapters", "chapters")}
                        {isAdmin && (
                            <>
                                {item("/dashboard/document-types", "Document Types", "types")}
                                {item("/dashboard/mission-types", "Mission Types", "list")}
                                {item("/dashboard/terms", "School Terms", "terms")}
                                {seesIssueLog && item("/dashboard/issue-types", "Issue Types", "types")}
                            </>
                        )}
                        {item("/dashboard/zones", "Zones", "zones")}
                        {isSuperAdmin && item("/dashboard/roles", "Roles", "roles")}
                    </ul>

                    <hr />
                    <ul className="menu-list">
                        {item("/dashboard/profile", "Profile", "profile", { exact: true })}
                        {item("/dashboard/profile/reset-password", "Reset Password", "password", { exact: true })}
                        <li>
                            <Link to="/logout" data-tip="Log Out" aria-label="Log Out">
                                <MenuIcon name="logout" />
                                <span className="cf-menu-label">Log Out</span>
                            </Link>
                        </li>
                    </ul>
                </div>
            </aside>
        </>
    );
}

export default Sidebar;
